import type { ScheduleDay } from '../types/schedule.types';
import { convertRailsToIANATimezone } from '../event_dashboard/utils/timezoneUtils';

/**
 * Get the optimal default day to display for a schedule
 * @param scheduleDays Array of schedule days
 * @param eventTimeZone The event's timezone (IANA format)
 * @returns The optimal day to display (current, next upcoming, or last day)
 */
export const getDefaultScheduleDay = (
  scheduleDays: ScheduleDay[],
  eventTimeZone?: string
): ScheduleDay | null => {
  if (scheduleDays.length === 0) return null;

  // Get current date in event timezone
  // If no timezone provided, use UTC to avoid timezone conversion issues
  let today: string;
  
  if (eventTimeZone) {
    try {
      // Convert Rails timezone to IANA if needed
      const ianaTimezone = convertRailsToIANATimezone(eventTimeZone);
      today = new Date().toLocaleDateString('en-CA', { timeZone: ianaTimezone });
    } catch (error) {
      // If timezone conversion fails, fall back to UTC
      console.warn('Failed to convert timezone, falling back to UTC:', error);
      today = new Date().toISOString().split('T')[0];
    }
  } else {
    today = new Date().toISOString().split('T')[0];
  }

  // Find today's schedule
  const todaySchedule = scheduleDays.find(day => day.date === today);
  if (todaySchedule) return todaySchedule;

  // Find the nearest upcoming day
  const upcomingDays = scheduleDays.filter(day => day.date > today);
  if (upcomingDays.length > 0) {
    // Return the first upcoming day (assumes days are sorted)
    return upcomingDays[0];
  }

  // No current or future days, return the last day
  return scheduleDays[scheduleDays.length - 1];
};